import axios from 'axios';
import {
  USER_INFO, ADD_TO_NETWORK, EDIT_SPAM_LIST,
  UNANSWERED_NETWORK_REQUEST_COUNT, DECREMENT_UNANSWERED_NETWORK_REQUEST_COUNT,
  RESET,
} from './types';
import { getOwnerId } from '../../../utils/getOwnerId';
import api from '../../../utils/api';

// In-flight deduplication: concurrent callers for the same key share one Promise.
// Completed cache: sequential callers skip re-fetching already-loaded data.
// Keys in use:
//   'userInvoices:{id}'    — fetchUserInvoices(id)
//   'searchCustomer:{name}:{orderBy}' — searchCustomer({ name, orderBy })
//   'userInfo'          — fetchUserInfo
//   'userPublicInfo:{id}'    — fetchUserPublicInfo(id)
//   'searchNetworkUser:{...}' — searchNetworkUser(query)
const _pendingActions = {};
const _completedActions = {};

export default {
  // Reuse the cached record only when it belongs to the requested customer,
  // otherwise a re-login as a different user would be served the previous
  // user's profile.
  async fetchUserInfo({ commit, state }, userId = '') {
    const targetId = userId || getOwnerId();
    if (state.userInfo && String(state.userInfo.customer_id) === String(targetId)) {
      return state.userInfo;
    }
    const key = 'userInfo';
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.user.fetchUserInfo(targetId));
        commit(USER_INFO, { info: data });
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  // Called on logout: state and the caches above are user-scoped and must not
  // outlive the session.
  reset({ commit }) {
    Object.keys(_pendingActions).forEach(k => delete _pendingActions[k]);
    Object.keys(_completedActions).forEach(k => delete _completedActions[k]);
    commit(RESET);
  },
  async fetchUserInvoices(_, id) {
    const key = `userInvoices:${id}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.user.fetchUserInvoices(id));
        _completedActions[key] = data;
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async editUserInfo({ commit }, editInfo) {
    const { data } = await axios(api.user.editUserInfo(editInfo, getOwnerId()));
    commit(USER_INFO, { info: data });
  },
  async changePassword({}, changePasswords) {
    await axios(api.user.changePassword(changePasswords));
  },
  async forgotPassword({}, data) {
    await axios(api.user.forgotPassword(data));
  },
  async fetchUserPublicInfo({ dispatch }, id) {
    const ownerId = getOwnerId();
    const customerId = id || ownerId;
    // The owner's own record is fetched + cached by `fetchUserInfo` (via
    // UserProfile.vue). Funnel any owner request through it so all
    // /customers/{ownerId}/ callers share one network request instead of
    // fetchUserPublicInfo and fetchUserInfo each issuing their own.
    if (String(customerId) === String(ownerId)) {
      return dispatch('fetchUserInfo');
    }
    const key = `userPublicInfo:${customerId}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.user.fetchUserPublicInfo(customerId));
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async searchNetworkUser(_, searchInfo) {
    const searchQuery = searchInfo;
    searchQuery.customerId = searchInfo.customerId || getOwnerId();
    const key = `searchNetworkUser:${searchQuery.customerId}:${searchQuery.companyId || ''}:${searchQuery.memberName || ''}:${searchQuery.membersPage || ''}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.user.searchNetworkUser(searchQuery));
        return data;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  async searchOnContacts(_, searchInfo) {
    const searchQuery = searchInfo;
    searchQuery.customerId = searchInfo.customerId || getOwnerId();
    const { data } = await axios(api.user.searchOnContacts(searchQuery));
    return data.results;
  },
  async fetchAllUsers({}, { page = 1, orderBy = null } = {}) {
    const { data } = await axios(api.user.fetchAllUsers(page, orderBy));
    return data;
  },
  async fetchInvitationRequests({ commit }, page = 1) {
    const { data } = await axios(
      api.user.fetchInvitationRequests(getOwnerId(), page),
    );
    commit(UNANSWERED_NETWORK_REQUEST_COUNT, data);
    return data;
  },
  async fetchSentInvitationRequests({}, page = 1) {
    const { data } = await axios(
      api.user.sentInvitationRequests(getOwnerId(), page),
    );
    return data;
  },
  // TODO check this endpoint especially commit an return data
  async userInvitationRequest({ commit }, invitationData) {
    const { data } = await axios(
      api.user.userInvitationRequest(invitationData),
    );
    commit(ADD_TO_NETWORK, data);
    return data;
  },
  async approveInvitationRequest({ commit }, invitationUrl) {
    const { data } = await axios(api.user.approveInvitationRequest(invitationUrl));
    commit(DECREMENT_UNANSWERED_NETWORK_REQUEST_COUNT);
    return data;
  },
  async refuseInvitationRequest({ commit }, invitationUrl) {
    await axios(api.user.refuseInvitationRequest(invitationUrl));
    commit(DECREMENT_UNANSWERED_NETWORK_REQUEST_COUNT);
  },
  async userNetwork({}, { page = 1, orderBy = null } = {}) {
    const { data } = await axios(api.user.userNetwork(page, orderBy));
    return data;
  },
  async deleteFromNetwork({}, deleteUrl) {
    const data = await axios(api.user.deleteFromNetwork(deleteUrl));
    return data;
  },
  async deleteMember({}, deleteUrl) {
    const data = await axios(api.user.deleteMember(deleteUrl));
    return data;
  },
  async searchCustomer(_, { name, orderBy = null } = {}) {
    // orderBy is part of the key: without it a re-search under a new sort would
    // be handed the previous ordering's in-flight promise.
    const key = `searchCustomer:${name}:${orderBy || ''}`;
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.user.searchCustomer(name, orderBy));
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
  // Returns the whole DRF envelope ({ count, next, previous, results }) so
  // callers can end pagination on `next` instead of guessing from an empty
  // page. Deliberately no _pendingActions dedup: that key is the name alone,
  // so two pages of the same keyword would collide.
  async searchCustomersPage({}, payload) {
    const { data } = await axios(api.user.searchCustomersPage(payload));
    return data;
  },
  async userRegister(_, userData) {
    const { data } = await axios(api.user.userRegister(userData));
    return data;
  },
  async findCustomers(_, findData) {
    const { data } = await axios(api.user.findCustomers(findData));
    return data;
  },
  async addRemoveSpam({ commit }, spamData) {
    const { data } = await axios(api.user.addRemoveSpam(spamData));
    commit(EDIT_SPAM_LIST, spamData);
    return data;
  },
};
